"use client";

import { useCallback, useMemo, useState } from "react";
import { AlertTriangle, ExternalLink } from "lucide-react";
import toast from "react-hot-toast";
import {
  blocksGoogleOAuth,
  getExternalBrowserHelpSteps,
  getGoogleOAuthBlockedMessage,
  getInAppBrowserName,
  openCurrentPageInExternalBrowser,
} from "@/lib/utils/inAppBrowser";

export default function InAppBrowserGoogleBanner() {
  const browserName = useMemo(() => getInAppBrowserName(), []);
  const [opening, setOpening] = useState(false);

  const handleOpenExternal = useCallback(async () => {
    setOpening(true);
    try {
      const result = await openCurrentPageInExternalBrowser();
      if (result === "copy") {
        toast.success(
          "주소가 복사되었습니다. Safari 주소창에 붙여넣기 후 로그인해 주세요.",
          { duration: 8000 }
        );
      } else if (result === "intent") {
        toast.success("Chrome에서 페이지를 여는 중…", { duration: 4000 });
      } else {
        toast.error("브라우저를 열 수 없습니다. 메뉴에서 Safari로 열어주세요.");
      }
    } finally {
      setOpening(false);
    }
  }, []);

  if (!blocksGoogleOAuth()) return null;

  const steps = getExternalBrowserHelpSteps(browserName);

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-sm font-medium leading-snug">
            {getGoogleOAuthBlockedMessage(browserName)}
          </p>
          <ul className="text-xs text-amber-900/90 space-y-1 list-disc pl-4">
            {steps.map(step => (
              <li key={step}>{step}</li>
            ))}
          </ul>
          <button
            type="button"
            onClick={handleOpenExternal}
            disabled={opening}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-500 disabled:opacity-60"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {opening ? "여는 중…" : "브라우저에서 열기"}
          </button>
          <p className="text-[11px] text-amber-800/80">
            이메일·비밀번호 로그인은 이 화면에서도 가능합니다.
          </p>
        </div>
      </div>
    </div>
  );
}

/** 구글 로그인 클릭 시 인앱 브라우저면 true (호출 측에서 OAuth 중단) */
export async function guardGoogleOAuthInAppBrowser(): Promise<boolean> {
  if (!blocksGoogleOAuth()) return false;

  const browserName = getInAppBrowserName();
  toast.error(getGoogleOAuthBlockedMessage(browserName), { duration: 7000 });

  const result = await openCurrentPageInExternalBrowser();
  if (result === "copy") {
    toast.success(
      "주소가 복사되었습니다. Safari에서 붙여넣기 후 구글 로그인을 진행해 주세요.",
      { duration: 8000 }
    );
  }

  return true;
}
